/*! Mega menu by Notch — click-triggered full-screen navigation */
(function () {
  const DESKTOP_MQ = '(min-width: 990px)';
  /* Panel close: 250ms delay + 750ms sweep; content fade: 600ms — fallback exceeds full staged exit. */
  const CLOSE_FALLBACK_MS = 1800;
  const HEADER_OFFSET_FALLBACK = 80;
  const HEADER_EXTRA_GAP = 8;
  let openInstance = null;

  class MegaMenuNotch extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onBlockDeselect = this.onBlockDeselect.bind(this);
      this.onPanelTransitionEnd = this.onPanelTransitionEnd.bind(this);
      this.previouslyFocused = null;
      this.scrollLocked = false;
      this.overlayPortaled = false;
      this.closeGeneration = 0;
      this.closeTimer = null;
      this.pendingCloseOptions = null;
      this.afterCloseAction = null;
      this.panelsEnded = 0;
      this.panelsNeeded = 0;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.trigger = this.querySelector('[data-mmn-trigger]');
      this.overlay = this.querySelector('[data-mmn-overlay]');
      this.closeButtons = this.querySelectorAll('[data-mmn-close]');
      this.leftPanel = this.overlay ? this.overlay.querySelector('[data-mmn-left]') : null;
      this.rightPanel = this.overlay ? this.overlay.querySelector('[data-mmn-right]') : null;

      if (!this.trigger || !this.overlay) return;

      this.portalOverlay();
      this.trigger.addEventListener('click', this.onTriggerClick);
      this.closeButtons.forEach((btn) => btn.addEventListener('click', this.onCloseClick));
      this.overlay.addEventListener('click', this.onOverlayClick);

      this.mediaQuery = window.matchMedia(DESKTOP_MQ);
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.onResize);
      } else if (this.mediaQuery.addListener) {
        this.mediaQuery.addListener(this.onResize);
      }

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', this.onBlockSelect);
        this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    disconnectedCallback() {
      this.afterCloseAction = null;
      this.close({ silent: true });
      this.removePortaledOverlay();
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
      this.closeButtons && this.closeButtons.forEach((btn) => btn.removeEventListener('click', this.onCloseClick));
      if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
      if (this.mediaQuery) {
        if (this.mediaQuery.removeEventListener) {
          this.mediaQuery.removeEventListener('change', this.onResize);
        } else if (this.mediaQuery.removeListener) {
          this.mediaQuery.removeListener(this.onResize);
        }
      }
      if (window.Shopify && Shopify.designMode) {
        this.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
      this.initialized = false;
    }

    portalOverlay() {
      if (!this.overlay || this.overlayPortaled) return;

      const hostStyle = this.getAttribute('style');
      if (hostStyle) {
        const overlayStyle = this.overlay.getAttribute('style') || '';
        const merged = overlayStyle.includes('--mmn-left-width')
          ? overlayStyle
          : `${hostStyle};${overlayStyle}`;
        this.overlay.setAttribute('style', merged);
      }

      document.body.appendChild(this.overlay);
      this.overlayPortaled = true;
    }

    removePortaledOverlay() {
      this.clearCloseHandlers();
      if (this.overlay && this.overlay.parentNode) {
        this.overlay.parentNode.removeChild(this.overlay);
      }
      this.overlayPortaled = false;
    }

    get isDesktop() {
      return window.matchMedia(DESKTOP_MQ).matches;
    }

    onTriggerClick(event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.isDesktop) return;
      /* Don't reopen while a link-driven close→navigate is in progress. */
      if (this.afterCloseAction) return;
      if (this.classList.contains('is-open')) {
        this.close();
      } else {
        this.open();
      }
    }

    onCloseClick(event) {
      event.preventDefault();
      this.afterCloseAction = null;
      this.close();
    }

    /**
     * Intercept normal in-menu link clicks so the exit animation completes
     * before navigation / same-page anchor scroll. Modified clicks pass through.
     */
    onOverlayClick(event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = event.target.closest && event.target.closest('a[href]');
      if (!link || !this.overlay.contains(link)) return;
      if (link.closest('[data-mmn-close]')) return;

      const target = (link.getAttribute('target') || '').toLowerCase();
      if (target && target !== '_self') return;
      if (link.hasAttribute('download')) return;

      const rawHref = link.getAttribute('href');
      if (!rawHref || rawHref === '#') return;
      if (/^(mailto:|tel:|javascript:)/i.test(rawHref)) return;

      /* Already closing toward a navigation — ignore rapid double clicks. */
      if (this.afterCloseAction) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const absoluteUrl = link.href;
      this.afterCloseAction = () => {
        this.followLinkAfterClose(absoluteUrl);
      };

      this.close();
    }

    followLinkAfterClose(absoluteUrl) {
      let url;
      try {
        url = new URL(absoluteUrl, window.location.href);
      } catch (e) {
        window.location.assign(absoluteUrl);
        return;
      }

      const sameOrigin = url.origin === window.location.origin;
      const samePath = url.pathname === window.location.pathname;
      const hash = url.hash || '';

      if (sameOrigin && samePath) {
        if (hash.length > 1) {
          this.scrollToSamePageAnchor(hash);
        }
        return;
      }

      window.location.assign(url.href);
    }

    measureHeaderOffset() {
      let total = 0;
      const groups = document.querySelectorAll('.shopify-section-group-header-group');

      if (groups.length) {
        groups.forEach((group) => {
          const pos = window.getComputedStyle(group).position;
          if (pos === 'sticky' || pos === 'fixed' || group.querySelector('sticky-header')) {
            total += group.offsetHeight;
          }
        });
      }

      if (!total) {
        const header = document.querySelector('sticky-header, .header-wrapper, .shopify-section-header');
        if (header) total = header.offsetHeight;
      }

      return total || HEADER_OFFSET_FALLBACK;
    }

    computeAnchorTop(el) {
      const offset = this.measureHeaderOffset();
      const smt = parseFloat(window.getComputedStyle(el).scrollMarginTop) || 0;
      const applied = Math.max(offset + HEADER_EXTRA_GAP, smt);
      return Math.max(el.getBoundingClientRect().top + window.pageYOffset - applied, 0);
    }

    scrollToSamePageAnchor(hash) {
      let id;
      try {
        id = decodeURIComponent(hash.replace(/^#/, ''));
      } catch (e) {
        id = hash.replace(/^#/, '');
      }

      const el = id ? document.getElementById(id) : null;

      if (window.history && history.pushState) {
        history.pushState(null, '', hash);
      } else {
        window.location.hash = hash;
      }

      if (!el) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: this.computeAnchorTop(el),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        this.afterCloseAction = null;
        this.close();
      }
    }

    onResize() {
      if (!this.isDesktop && this.classList.contains('is-open')) {
        this.afterCloseAction = null;
        this.close({ silent: true });
      }
    }

    onBlockSelect(event) {
      const host = event.target && event.target.closest
        ? event.target.closest('mega-menu-notch')
        : null;
      if (host !== this && event.target !== this) return;
      if (window.Shopify && Shopify.designMode) {
        this.afterCloseAction = null;
        this.open({ fromEditor: true });
      }
    }

    onBlockDeselect(event) {
      const host = event.target && event.target.closest
        ? event.target.closest('mega-menu-notch')
        : null;
      if (host !== this && event.target !== this) return;
      if (window.Shopify && Shopify.designMode) {
        this.afterCloseAction = null;
        this.close({ fromEditor: true, silent: true });
      }
    }

    clearCloseHandlers() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      if (this.leftPanel) {
        this.leftPanel.removeEventListener('transitionend', this.onPanelTransitionEnd);
      }
      if (this.rightPanel) {
        this.rightPanel.removeEventListener('transitionend', this.onPanelTransitionEnd);
      }
      this.panelsEnded = 0;
      this.panelsNeeded = 0;
      this.pendingCloseOptions = null;
    }

    open(options = {}) {
      if (!this.isDesktop && !options.fromEditor) return;
      if (this.afterCloseAction) return;

      this.closeGeneration += 1;
      this.clearCloseHandlers();

      if (openInstance && openInstance !== this) {
        openInstance.close({ silent: true });
      }

      openInstance = this;
      this.previouslyFocused = document.activeElement;
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.overlay.classList.remove('is-closing');
      this.overlay.hidden = false;

      requestAnimationFrame(() => {
        this.overlay.classList.add('is-active');
      });

      this.lockScroll();
      document.addEventListener('keydown', this.onKeydown);

      const focusTarget = this.overlay.querySelector('[data-mmn-close]') || this.overlay;
      if (typeof trapFocus === 'function') {
        trapFocus(this.overlay, focusTarget);
      } else if (focusTarget && focusTarget.focus) {
        focusTarget.focus();
      }
    }

    close(options = {}) {
      const isOpen = this.classList.contains('is-open');
      const isVisible = this.overlay && !this.overlay.hidden;

      if (!isOpen && !isVisible) {
        /* Still run a queued post-close action if cleanup already finished. */
        this.runAfterCloseAction();
        return;
      }

      const generation = ++this.closeGeneration;
      this.clearCloseHandlers();
      this.pendingCloseOptions = options;

      this.classList.remove('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this.onKeydown);

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (options.silent || reduceMotion || !this.overlay) {
        this.finishClose(options, generation);
        return;
      }

      this.overlay.classList.add('is-closing');
      this.overlay.classList.remove('is-active');

      this.panelsEnded = 0;
      this.panelsNeeded = 0;
      [this.leftPanel, this.rightPanel].forEach((panel) => {
        if (!panel) return;
        this.panelsNeeded += 1;
        panel.addEventListener('transitionend', this.onPanelTransitionEnd);
      });

      if (this.panelsNeeded === 0) {
        this.finishClose(options, generation);
        return;
      }

      this.closeTimer = window.setTimeout(() => {
        this.finishClose(options, generation);
      }, CLOSE_FALLBACK_MS);
    }

    onPanelTransitionEnd(event) {
      if (event.target !== this.leftPanel && event.target !== this.rightPanel) return;
      if (event.propertyName && event.propertyName !== 'transform') return;

      this.panelsEnded += 1;
      if (this.panelsEnded < this.panelsNeeded) return;

      this.finishClose(this.pendingCloseOptions || {}, this.closeGeneration);
    }

    finishClose(options = {}, generation) {
      if (generation != null && generation !== this.closeGeneration) return;
      if (this.classList.contains('is-open')) return;
      if (this.closeCompletedGeneration === generation) return;
      this.closeCompletedGeneration = generation;

      this.clearCloseHandlers();

      if (this.overlay) {
        this.overlay.classList.remove('is-active');
        this.overlay.classList.remove('is-closing');
        this.overlay.hidden = true;
      }

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(options.silent ? null : this.previouslyFocused || this.trigger);
      } else if (!options.silent && this.previouslyFocused && this.previouslyFocused.focus) {
        this.previouslyFocused.focus();
      } else if (!options.silent && this.trigger) {
        this.trigger.focus();
      }

      this.unlockScroll();
      if (openInstance === this) openInstance = null;

      if (typeof options.onComplete === 'function') {
        options.onComplete();
      }

      this.runAfterCloseAction();
    }

    runAfterCloseAction() {
      const action = this.afterCloseAction;
      this.afterCloseAction = null;
      if (typeof action === 'function') {
        action();
      }
    }

    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;
      document.documentElement.classList.add('mmn-scroll-lock');
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      if (!openInstance || openInstance === this) {
        document.documentElement.classList.remove('mmn-scroll-lock');
      }
    }
  }

  if (!customElements.get('mega-menu-notch')) {
    customElements.define('mega-menu-notch', MegaMenuNotch);
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('mega-menu-notch').forEach((el) => {
      if (typeof el.connectedCallback === 'function' && !el.initialized) {
        el.connectedCallback();
      }
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target;
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('mega-menu-notch').forEach((el) => {
      el.afterCloseAction = null;
      if (typeof el.close === 'function') el.close({ silent: true });
      if (typeof el.removePortaledOverlay === 'function') el.removePortaledOverlay();
    });
    document.querySelectorAll('.mmn__overlay').forEach((overlay) => {
      const hostId = overlay.id && overlay.id.replace(/-panel$/, '');
      if (hostId && !document.getElementById(hostId) && overlay.parentNode === document.body) {
        overlay.remove();
      }
    });
  });
})();
